import type { FC } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import settings from 'src/settings';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import type { AfterLoginCallback } from 'src/components/app/context/LoginConsumer';
import LoginConsumer from 'src/components/app/context/LoginConsumer';
import ErrorMessage from 'src/components/common/Messages/ErrorMessage';

import { styles } from './index.styles';

interface GoogleLoginComponentProps {
  onDone: () => void;
  afterLogin: AfterLoginCallback;
}

const GoogleLoginComponent: FC<GoogleLoginComponentProps> = ({
  onDone,
  afterLogin,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  const { t } = useTranslation('login');
  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse: any) => getApiToken(tokenResponse.code),
    onError: (error: any) => setErrorMessage(error),
    flow: 'auth-code',
  });

  const getApiToken = (code: string) =>
    HttpService.post(apiPaths.ACCOUNT.LOGIN_SSO, {
      code: code,
    })
      .then((response) => {
        afterLogin(response.data.key, onDone);
        return true;
      })
      .catch((error) => {
        setErrorMessage(error.response.data.non_field_errors[0]);
        return false;
      });
  const startSso = () => {
    setIsLoading(true);
    googleLogin();
    setIsLoading(false);
  };

  return (
    <Box sx={styles.googleLogin}>
      <LoadingButton
        loading={isLoading}
        onClick={startSso}
        variant="cta"
        fullWidth
      >
        {t('oneClickLoginButton')}
      </LoadingButton>
      {errorMessage && (
        <ErrorMessage sx={styles.errorMsg}>{errorMessage}</ErrorMessage>
      )}
    </Box>
  );
};

const GoogleLoginWrapper = ({ onDone }: { onDone: () => void }) => (
  <LoginConsumer
    render={(afterLogin: AfterLoginCallback) => (
      <GoogleOAuthProvider clientId={settings.VITE_APP_GOOGLE_CLIENT_ID}>
        <GoogleLoginComponent onDone={onDone} afterLogin={afterLogin} />
      </GoogleOAuthProvider>
    )}
  />
);

export default GoogleLoginWrapper;
